import * as toGeoJSON from "@tmcw/togeojson";
import JSZip from "jszip";

type CustomShape = {
    type: "circle" | "polygon" | "polyline";
    center?: number[]; // [lng, lat] for circles
    radius?: number; // radius in meters for circles
    geometry?: number[][]; // [[lng, lat], ...] for polygons/polylines
};

/**
 * Parse KML or KMZ file and convert to CustomShape array
 */
export async function parseKMLFile(file: File): Promise<CustomShape[]> {
    const extension = file.name.split(".").pop()?.toLowerCase();

    let kmlText: string;

    if (extension === "kmz") {
        // KMZ is a zipped KML file
        kmlText = await extractKMLFromKMZ(file);
    } else if (extension === "kml") {
        kmlText = await file.text();
    } else {
        throw new Error("File must be KML or KMZ format");
    }

    // Parse KML to GeoJSON
    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlText, "text/xml");
    const geoJSON = toGeoJSON.kml(kmlDoc) as any;

    // Convert GeoJSON to CustomShape array
    const shapes: CustomShape[] = [];

    if (geoJSON.type === "FeatureCollection") {
        for (const feature of geoJSON.features) {
            const shape = convertGeometryToCustomShape(feature.geometry, feature.properties);
            if (shape) {
                shapes.push(shape);
            }
        }
    } else if (geoJSON.type === "Feature") {
        const shape = convertGeometryToCustomShape(geoJSON.geometry, geoJSON.properties);
        if (shape) {
            shapes.push(shape);
        }
    }

    if (shapes.length === 0) {
        throw new Error("No valid geometries found in KML file");
    }

    return shapes;
}

/**
 * Extract KML from KMZ (zipped) file
 */
async function extractKMLFromKMZ(file: File): Promise<string> {
    const zip = new JSZip();
    const contents = await zip.loadAsync(file);

    // Look for .kml file in the archive
    const kmlFile = Object.keys(contents.files).find(
        (filename) => filename.toLowerCase().endsWith(".kml")
    );

    if (!kmlFile) {
        throw new Error("No KML file found in KMZ archive");
    }

    return await contents.files[kmlFile].async("text");
}

/**
 * Convert GeoJSON geometry to CustomShape
 * @param geometry - GeoJSON geometry object
 * @param properties - KML properties (ExtendedData converted to properties by toGeoJSON)
 */
function convertGeometryToCustomShape(geometry: any, properties?: any): CustomShape | null {
    if (!geometry || !geometry.type) {
        return null;
    }

    // Extract radius from properties if available (default to 5000m)
    // Look for 'radius' property (case-insensitive)
    let customRadius = 5000;
    if (properties) {
        const radiusKey = Object.keys(properties).find(key => key.toLowerCase() === "radius");
        if (radiusKey) {
            const radiusValue = parseFloat(properties[radiusKey]);
            if (!isNaN(radiusValue) && radiusValue > 0) {
                customRadius = radiusValue;
            }
        }
    }

    switch (geometry.type) {
        case "Point":
            // Convert point to a circle with custom or default radius
            // GeoJSON uses [lng, lat], which matches our backend format
            return {
                type: "circle",
                center: [geometry.coordinates[0], geometry.coordinates[1]], // [lng, lat]
                radius: customRadius
            };

        case "LineString":
            // Convert to polyline
            // GeoJSON uses [lng, lat], which matches our backend format
            return {
                type: "polyline",
                geometry: geometry.coordinates.map((coord: number[]) => [coord[0], coord[1]]) // [lng, lat]
            };

        case "Polygon":
            // Use outer ring only (ignore holes)
            // GeoJSON uses [lng, lat], which matches our backend format
            return {
                type: "polygon",
                geometry: geometry.coordinates[0].map((coord: number[]) => [coord[0], coord[1]]) // [lng, lat]
            };

        case "MultiPoint":
            // Take the first point
            if (geometry.coordinates.length > 0) {
                return {
                    type: "circle",
                    center: [geometry.coordinates[0][0], geometry.coordinates[0][1]], // [lng, lat]
                    radius: customRadius
                };
            }
            return null;

        case "MultiLineString": {
            // Flatten all line strings into one polyline
            const allCoords = geometry.coordinates.flat();
            return {
                type: "polyline",
                geometry: allCoords.map((coord: number[]) => [coord[0], coord[1]]) // [lng, lat]
            };
        }

        case "MultiPolygon":
            // Take the first polygon's outer ring
            if (geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
                return {
                    type: "polygon",
                    geometry: geometry.coordinates[0][0].map((coord: number[]) => [coord[0], coord[1]]) // [lng, lat]
                };
            }
            return null;

        default:
            console.warn(`Unsupported geometry type: ${geometry.type}`);
            return null;
    }
}
